import { Theme as AugmentedTheme, useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  Box,
  HtmlTrans,
  MUIDataTable,
  MUIDataTableColumn,
  Pill,
  PillVariant,
  Typography,
  useDataTable,
} from 'web-library';
import {
  ApplicationStatus,
  CandidateRegisteredExamListingResponse,
} from '../../../api/candidate/exam/model';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import DeleteDraftDialog, {
  IDialogStateHandler,
} from '../../../modules/examRegistration/components/listing/DeleteDraftDialog';
import {
  GroupRegisteredExamModelResponse,
  useGetCoordinatorGroupExamRegistrations,
} from '../../../api-refactored/group-exam-registration/useGetCoordinatorGroupExamRegistrations';
import CoordinatorExamListingActionButtons from './CoordinatorExamListingActionButtons';
import { useGetClosedExamTypes } from '../../../modules/examRegistration/logic/useClosedExamTypes';
import { GET_GROUP_EXAM_REGISTRATIONS_QUERY_KEY } from '../../../constants/queryKeys';
import ProceedGroupSupplementaryModal from '../../../modules/groupExamRegistration/components/ProceedGroupSupplementaryModal';
import RouteKey from '../../../routes/key';

import {typeTheme} from "web-library/theme/helper/tssHelper"
// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  '@global': {
    '#group-exam-registration-listing': {
      '& .MuiTableRow-root': {
        verticalAlign: 'top',
        '&:not(:last-child)': {
          borderBottom: `1px solid ${theme.color.border.borderFocused} !important`,
        },
        '& .MuiTableCell-root': {
          '&[data-colindex="0"]': {
            minWidth: 200,
          },
        },
      },
      '& .MuiTableBody-root': {
        '& .MuiButtonBase-root': {
          height: '20px',
          padding: '12px',
        },
      },
    },
  },
  table: {
    marginBottom: theme.spacing(3),
  },
  alert: {
    marginBottom: theme.spacing(3),
  },
  amendOrViewButton: {
    color: theme.color.link.linkDefault01,
    padding: '0px !important',
    marginRight: theme.spacing(6),
    width: 'max-content',
  },
  tabsHeader: {
    '& .MuiTab-root': {
      textTransform: 'none',
    },
    marginBottom: theme.spacing(4),
    '& button': {
      maxWidth: '326px',
    },
  },
  examLevelCol: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(3),
    },
  },
  instruction: {
    marginBottom: theme.spacing(3),
  },
  iconContainer: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  deleteButton: {
    color: theme.color.icon.iconTrash,
    whiteSpace: 'nowrap',
    padding: `${theme.spacing(1)} !important`,
    minHeight: 0,
    minWidth: 0,
    text: {
      padding: '0 !important',
    },
  },
  actionButtons: {
    display: 'flex',
    flexDirection: 'row',
    width: 'max-content',
    alignItems: 'center',
  },
  link: {
     ...typeTheme(theme, 'buttonSmall'),
  },
  accessArrangement: {
    marginTop: theme.spacing(3),
  },
  viewTransactionLink: {
    fontWeight: 800,
  },
}));

const { exam } = RouteKey.child;
const { coordinatorExamRegistrations, bulkUploadCandidates } = RouteKey.exam.child;

const coordinatorListingDataColumns = (
  data: GroupRegisteredExamModelResponse[],
  classes: any,
  history: any,
  theme: AugmentedTheme,
  dialogStateHandler: IDialogStateHandler,
  t: TFunction<'translation'>
): MUIDataTableColumn[] => [
    {
      name: 'applicationID',
      label: 'Application ID',
      options: {
        sort: false,
      },
    },
    {
      name: 'examLevels',
      label: 'Exam Level(s)',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => (
          <>
            {data[dataIndex].examLevels && data[dataIndex].examLevels.length ? (
              data[dataIndex].examLevels.map((d) => (
                <Typography
                  noWrap
                  variant="body2"
                  className={classes.examLevelCol}
                  key={`key-${d.examLevel}`}
                >
                  {d.examLevel}
                </Typography>
              ))
            ) : (
              <Typography variant="body2">{data[dataIndex].examType}</Typography>
            )}
          </>
        ),
      },
    },
    {
      name: 'coordinatorName',
      label: 'Coordinator Name',
      options: {
        sort: false,
      },
    },
    {
      name: 'applicationDateTime',
      label: 'Application Date/Time',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          if (data[dataIndex].applicationDateTime) {
            return (
              <Typography variant="body2">
                {stringToLongDateAndShortTime(data[dataIndex].applicationDateTime || '')}
              </Typography>
            );
          }
          return <>-</>;
        },
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        sort: false,
        customBodyRenderLite: (dataIndex: number) => {
          if (data[dataIndex].applicationStatus) {
            const { applicationStatus } = data[dataIndex];
            return getApplicationStatus(applicationStatus);
          }
          return <Pill variant="info" label="" />;
        },
      },
    },
    {
      name: 'viewDetail',
      label: t('exam->>ViewRegisteredExamsListing.Actions'),
      options: {
        sort: false,
        setCellProps: () => ({ style: { width: '20%' } }),
        customBodyRenderLite: (dataIndex: number, rowIndex: number) =>
          CoordinatorExamListingActionButtons({
            data,
            styling: {
              classes,
              theme,
            },
            hooks: {
              history,
              t,
            },
            MUIDataIndex: {
              dataIndex,
              rowIndex,
            },
            dialogStateHandler,
          }),
      },
    },
  ];

const getApplicationStatus = (applicationStatus: string) => {
  let applicationStatusDisplay = applicationStatus;
  let variant: PillVariant = 'info';
  if (applicationStatus === ApplicationStatus.SUCCESS.toUpperCase()) {
    applicationStatusDisplay = 'File Processing - Success';
    variant = 'positive';
  }

  if (applicationStatus === ApplicationStatus.REGISTERED.toUpperCase()) {
    applicationStatusDisplay = 'Registered';
    variant = 'positive';
  }

  if (applicationStatus === ApplicationStatus.PROCESSING.toUpperCase()) {
    applicationStatusDisplay = 'File Processing';
  }

  if (applicationStatus === ApplicationStatus.ERROR.toUpperCase()) {
    applicationStatusDisplay = 'File Processing - Error';
    variant = 'negative';
  }

  if (applicationStatus === ApplicationStatus.SUBMITTED) {
    applicationStatusDisplay = 'Submitted';
    variant = 'positive';
  }

  return <Pill variant={variant} label={applicationStatusDisplay} />;
};

const isRegisteredExamOrEligibleStatus = (item: CandidateRegisteredExamListingResponse) =>
  item.applicationStatus === ApplicationStatus.REGISTERED.toUpperCase() ||
  item.applicationStatus === ApplicationStatus.SUCCESS ||
  item.applicationStatus === ApplicationStatus.ERROR ||
  item.applicationStatus === ApplicationStatus.PROCESSING;

const CoordinatorExamRegistrationsListing = () => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory<any>();
  const theme = useTheme();
  const { state, dispatch } = useDataTable();
  const isFetching = false;
  const { data } = useGetCoordinatorGroupExamRegistrations({});
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isDeleteDraftDialogOpen, setDeleteDraftDialogOpen] = useState(false);
  const [proceedSupplementaryModal, setProceedSupplementaryModal] = useState<boolean>(false);
  const [deleteUuid, setDeleteUuid] = useState<string>('');
  const [deleteExamType, setDeleteExamType] = useState<string>('');

  const { isWindowOpenedCallback: isWindowOpened } = useGetClosedExamTypes(data);

  const isHavingRegisteredExam = useCallback(() => {
    if (Array.isArray(data)) {
      return data.some(isRegisteredExamOrEligibleStatus);
    }
    return false;
  }, [data]);

  const isHavingDraftApplication = useCallback(() => {
    if (Array.isArray(data)) {
      return data.some(
        (item: CandidateRegisteredExamListingResponse) =>
          item.applicationStatus === ApplicationStatus.DRAFT.toUpperCase() ||
          item.applicationStatus === ApplicationStatus.SUCCESS ||
          item.applicationStatus === ApplicationStatus.ERROR ||
          item.applicationStatus === ApplicationStatus.PROCESSING
      );
    }
    return false;
  }, [data]);

  useEffect(() => {
    if (!isWindowOpened()?.opened && isHavingRegisteredExam() && isHavingDraftApplication()) {
      setIsErrorMsg(true);
    }
  }, [isWindowOpened, isHavingRegisteredExam, isHavingDraftApplication]);

  return (
    <>
      {data && (
        <>
          {isErrorMsg && (
            <Alert
              className={classes.alert}
              severity="error"
              message={t('exam->>ViewRegisteredExamsListing.RegistrationUnavailable', {
                closedExamType: isWindowOpened()?.closedExamType.join(', '),
              })}
              variant="filled"
            />
          )}

          <Box id="group-exam-registration-listing">
            <MUIDataTable
              state={state}
              dispatch={dispatch}
              isLoading={isFetching}
              columns={coordinatorListingDataColumns(
                data,
                classes,
                history,
                theme,
                {
                  isDeleteDraftDialogOpen,
                  setDeleteDraftDialogOpen,
                  setProceedSupplementaryModal,
                  setDeleteUuid,
                  setDeleteExamType,
                },
                t
              )}
              data={data}
              emptyDataLabel={t('exam->>ViewRegisteredExamsListing.HaveNotRegistered')}
              emptyDataLabelAdditional={
                <HtmlTrans i18nKey="exam->>ViewRegisteredExamsListing.ExamRegistrationInstruction" />
              }
              options={{
                sort: true,
              }}
            />
          </Box>

          <DeleteDraftDialog
            draftId={deleteUuid}
            dialogStateHandler={{
              isDeleteDraftDialogOpen,
              setDeleteDraftDialogOpen,
              setProceedSupplementaryModal,
              setDeleteUuid,
              setDeleteExamType,
            }}
            queryKeyToInvalidate={GET_GROUP_EXAM_REGISTRATIONS_QUERY_KEY}
          />
          <ProceedGroupSupplementaryModal
            modelOpen={proceedSupplementaryModal}
            setModelOpen={setProceedSupplementaryModal}
            callbackMethod={(uuid) => {
              history.push(
                `/${exam}/${coordinatorExamRegistrations.route}/${uuid}/${bulkUploadCandidates.route}`
              );
            }}
            draftUuid={deleteUuid}
            examType={deleteExamType}
          />
        </>
      )}
    </>
  );
};

export default CoordinatorExamRegistrationsListing;
